/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import { io, ManagerOptions, Socket } from 'socket.io-client';
import { Decoder as IODecoder, Encoder as IOEncoder } from 'socket.io-parser';

import urlParse from 'url-parse';
import { ObservableMixin, priorities, global, Emitter } from 'ckeditor5/src/utils.js';

import Channel from './channel';
import { IToken, TokenChangeValueEventCallback } from '../types';

import User from './../users/user';

import version from './../version';
import CKEditorCloudServicesError from './../ckeditorcloudserviceserror';
import CKEditorCloudServicesServerError, { IPublicError } from './../ckeditorcloudservicesservererror';

import { Encoder, Decoder } from './parser/parser';
import { IMessage } from '../message';
import WebSocketGatewayRequestsManager from './requestsmanager';

const CONNECT_TIMEOUT: number = 5000;
const REQUEST_TIMEOUT: number = 20 * 1000;
const HTTPS_PORT: number = 443;
const HTTP_PORT: number = 80;
const WEB_SOCKET_PATH: string = '/ws-v2/ws';
const WEB_SOCKET_GATEWAY_URL: string = 'local.cs.dev:443/ws-v2';
const INITIAL_CONNECTION_ATTEMPTS: number = 3;

// `as const` guarantees the structure and its readonly state. The type can be inferred
// while direct type definition requires more bloated code
// eslint-disable-next-line @typescript-eslint/typedef
export const WEB_SOCKET_GATEWAY_STATES = {
	DISCONNECTED: 'disconnected',
	CONNECTING: 'connecting',
	CONNECTED: 'connected'
} as const;

export type IWebSocketGatewayStates = typeof WEB_SOCKET_GATEWAY_STATES;
export type WebSocketGatewayState = IWebSocketGatewayStates[keyof IWebSocketGatewayStates];

export type ConnectionProvider = ( url: string, options: IWebSocketGatewayConnectionOptions ) => ISocket;

export interface IWebSocketGatewayOptions {
	rejectUnauthorized?: boolean;

	autoReconnect?: boolean;

	agent?: unknown;

	onError?: ( error: unknown ) => void;

	timeout?: number;

	requestTimeout?: number;
}

export interface ISocket extends Socket {}

interface ISocketAuth {
	userId?: string;
	environmentId?: string;
	isAuthenticated?: boolean;
}

export interface IWebSocketGatewayConnectionOptions extends ManagerOptions {
	parser: {
		Decoder: ( new ( ...args: unknown[] ) => IODecoder );
		Encoder: ( new ( ...args: unknown[] ) => IOEncoder );
	};
}

interface IAuthMessage {
	environmentId: string;
	userId: string;
}

export interface IWebSocketGateway extends Emitter {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	_sendRequest<T extends IMessage>( serviceName: number, method: string, buffer: Uint8Array ): Promise<T>;

	// eslint-disable-next-line @typescript-eslint/naming-convention
	_getChannel( serviceName: string | number, channelId: string ): Channel;

	disconnect(): void;

	reconnect(): Promise<void>;

	me?: User;

	/**
	 * @deprecated - Use `socketId` property instead
	 */
	sessionId?: string;

	socketId?: string;

	state?: WebSocketGatewayState;
}

/**
 * Represents class for connecting to CKEditor Cloud Services WebSocket API.
 */
// eslint-disable-next-line new-cap
class WebSocketGateway extends /* #__PURE__ -- @preserve */ ObservableMixin() {
	private readonly _requestsManager: WebSocketGatewayRequestsManager;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	private readonly _url: urlParse<any>;

	private _socket?: ISocket;

	private _socketAuth?: ISocketAuth;

	private readonly _channels: Map<string | number, Channel>;

	private _connectionAttempt: number = 0;

	private readonly _token: IToken;

	private readonly _options: IWebSocketGatewayOptions;

	private readonly _connectionProvider: ConnectionProvider;

	private readonly _userFactory: ( wsGateway: IWebSocketGateway, id: string ) => Promise<User>;

	/**
	 * Represents currently logged user as taken from CMS (Token). Undefined value represents anonymous user.
	 */
	public declare me?: User;

	/**
	 * Id of an EndUser's socket.
	 */
	public declare socketId?: string;

	/**
	 * WebSocketGateway connection state.
	 */
	public declare state?: WebSocketGatewayState;

	/**
	 * Defines "disconnected" state. Returned by {@link WebSocketGateway#state}.
	 */
	public static readonly STATE_DISCONNECTED: string =
		/* #__PURE__ -- @preserve */ ( () => WEB_SOCKET_GATEWAY_STATES.DISCONNECTED )();

	/**
	 * Defines "connecting" state. Returned by {@link WebSocketGateway#state}.
	 */
	public static readonly STATE_CONNECTING: string =
		/* #__PURE__ -- @preserve */ ( () => WEB_SOCKET_GATEWAY_STATES.CONNECTING )();

	/**
	 * Defines "connected" state. Returned by {@link WebSocketGateway#state}.
	 */
	public static readonly STATE_CONNECTED: string =
		/* #__PURE__ -- @preserve */ ( () => WEB_SOCKET_GATEWAY_STATES.CONNECTED )();

	/**
	 * Returns highest priority for `change:state` handlers.
	 */
	// eslint-disable-next-line @typescript-eslint/naming-convention
	public static readonly _CHANGE_STATE_EVENT_PRIORITY: number =
		/* #__PURE__ -- @preserve */ ( () => priorities.get( 'highest' ) + 999999 )();

	public constructor(
		apiAddress: string,
		token: IToken,
		options?: IWebSocketGatewayOptions,
		connectionProvider?: ConnectionProvider,
		userFactory?: ( wsGateway: IWebSocketGateway, id: string ) => Promise<User>
	) {
		super();

		this._token = token;
		this._options = options ?? {};
		this._connectionProvider = connectionProvider ?? io;
		this._userFactory = userFactory ?? User.get;
		this._requestsManager = new WebSocketGatewayRequestsManager( this );
		this._channels = new Map();

		if ( !apiAddress ) {
			throw new TypeError( 'Api address must be provided.' );
		}

		if ( !this._token ) {
			throw new TypeError( 'Token must be provided.' );
		}

		if ( !this._options.requestTimeout ) {
			this._options.requestTimeout = REQUEST_TIMEOUT;
		}

		this._url = urlParse( apiAddress.replace( /^(?!(?:\w+:)?\/\/)/, 'https://' ) );

		this.set( 'state', WEB_SOCKET_GATEWAY_STATES.DISCONNECTED );

		this.set( 'socketId', undefined );

		this.set( 'me', undefined );

		this.on(
			'change:state',
			async ( event, name, value ) => {
				this._debugEvent( 'ws-gw:change:state', value );

				if ( value === WebSocketGateway.STATE_CONNECTED ) {
					try {
						this.me = await this._userFactory.call( User, this, this._socketAuth?.userId );
					} catch ( error ) {
						// ignore
					}

					return;
				}

				if ( value === WebSocketGateway.STATE_DISCONNECTED ) {
					return this._requestsManager.errorAll( new CKEditorCloudServicesError( 'Not connected.', this ) );
				}
			},
			{ priority: WebSocketGateway._CHANGE_STATE_EVENT_PRIORITY }
		);

		this.on( 'error', ( event, error ) => {
			if ( this._options.onError ) {
				this._options.onError( error );
			} else {
				console.error( error );
			}
		} );
	}

	/**
	 * Id of an EndUser's Session.
	 *
	 * @deprecated - Use `socketId` property instead
	 */
	public get sessionId(): string | undefined {
		return this.socketId;
	}

	/**
	 * Wait for fulfilled all sent requests.
	 * This method might be called before the call disconnect method to ensure all requests are fulfilled.
	 */
	public waitForAllRequests( time?: number ): Promise<void> {
		return this._requestsManager.waitForAllRequests( time );
	}

	/**
	 * Disconnects from WebSocket Gateway.
	 */
	public disconnect(): void {
		if ( this.state === WEB_SOCKET_GATEWAY_STATES.DISCONNECTED ) {
			return;
		}

		this._socket?.disconnect();
		this._socket = undefined;

		this.state = WEB_SOCKET_GATEWAY_STATES.DISCONNECTED;
	}

	/**
	 * Reconnects disconnected WebSocket Gateway.
	 * Only works if it is in disconnected {@link WebSocketGateway#state} otherwise returns resolved promise.
	 */
	public async reconnect(): Promise<void> {
		if ( !this._socket && this.state === WEB_SOCKET_GATEWAY_STATES.DISCONNECTED ) {
			await this._token.refreshToken();
			await this._connect();
		}
	}

	/**
	 * Connects to CKEditor Cloud Services.
	 *
	 *     const token = await Token.create( 'https://token-endpoint' );
	 *     const webSocketGateway = await WebSocketGateway.connect( token );
	 */
	public static async connect(
		token: IToken,
		apiAddress: string = WEB_SOCKET_GATEWAY_URL,
		options: IWebSocketGatewayOptions = {},
		provider: ConnectionProvider = io,
		userFactory: ( wsGateway: IWebSocketGateway, id: string ) => Promise<User> = User.get
	): Promise<WebSocketGateway> {
		const webSocketGateway: WebSocketGateway = new WebSocketGateway( apiAddress, token, options, provider, userFactory );

		await webSocketGateway._connect();

		return webSocketGateway;
	}

	/**
	 * Sends request to CKEditor Cloud Services.
	 */
	// eslint-disable-next-line @typescript-eslint/naming-convention
	public _sendRequest<TResponse extends IMessage>( serviceName: number, method: string, buffer: Uint8Array ): Promise<TResponse> {
		if ( !serviceName ) {
			throw new CKEditorCloudServicesError( '`serviceName` must be provided.', this );
		}

		if ( this.state !== WebSocketGateway.STATE_CONNECTED ) {
			throw new CKEditorCloudServicesError( 'Not connected.', this );
		}

		if ( !this._socketAuth || !this._socketAuth.isAuthenticated ) {
			throw new CKEditorCloudServicesError( 'Not authenticated.', this );
		}

		const envelopeBuffer: ArrayBuffer = new ArrayBuffer( buffer.length + 2 );
		const envelopeBufferView: Uint8Array = new Uint8Array( envelopeBuffer );

		envelopeBufferView[ 0 ] = serviceName;
		envelopeBufferView[ 1 ] = parseInt( method );

		envelopeBufferView.set( buffer, 2 );

		return this._emit<TResponse>( 1, envelopeBufferView );
	}

	/**
	 * Returns {@link Channel} associated with given service.
	 */
	// eslint-disable-next-line @typescript-eslint/naming-convention
	public _getChannel( serviceName: string | number, channelId: string ): Channel {
		const channelName: string = `${ serviceName }${ channelId }`;

		if ( !this._channels.has( channelName ) && this._socket ) {
			this._channels.set( channelName, new Channel( channelName, this, this._socket ) );
		}

		return this._channels.get( channelName )!;
	}

	private _connect(): Promise<void> {
		return new Promise( ( resolve, reject ) => {
			const socket: ISocket = this._setupSocket();

			const isFirstConnection: boolean = !this.socketId;

			if ( isFirstConnection ) {
				socket.io.on( 'reconnect_error', () => {
					this._debugEvent( 'reconnect_error' );

					this._reconnectionAttemptError( reject );
				} );
			}

			socket.once( 'connect', async () => {
				this._debugEvent( 'once-connect' );

				try {
					await this._onConnect();

					resolve();
				} catch ( error ) {
					reject( error );
				}
			} );

			socket.connect();
		} );
	}

	private _getPortByProtocol( protocol: string ): number {
		return [ 'http:', 'ws:' ].includes( protocol ) ? HTTP_PORT : HTTPS_PORT;
	}

	/**
	 * Creates unconnected Socket instance or returns already existing one.
	 */
	private _setupSocket(): ISocket {
		if ( this._socket ) {
			return this._socket;
		}

		const port: string | number = this._url.port || this._getPortByProtocol( this._url.protocol );

		const url: string = `${ this._url.protocol || 'https:' }//${ this._url.hostname }:${ port }`;

		const pathPrefix: string = this._url.pathname.match( /^\/.*\/ws/ ) ? this._url.pathname.split( '/ws' )[ 0 ] : '';

		const socket: ISocket = this._connectionProvider(
			url,
			{
				parser: { Encoder, Decoder },
				path: pathPrefix + WEB_SOCKET_PATH,
				transports: [ 'websocket' ],
				timeout: typeof this._options.timeout !== 'undefined' ? this._options.timeout : CONNECT_TIMEOUT,
				reconnection: typeof this._options.autoReconnect !== 'undefined' ? this._options.autoReconnect : true,
				reconnectionDelay: 1000,
				reconnectionDelayMax: 5000,
				rejectUnauthorized: typeof this._options.rejectUnauthorized !== 'undefined' ? this._options.rejectUnauthorized : true,
				query: {
					version
				},
				// @ts-ignore Problem with definition/types of socket.io - expected type http.Agent
				agent: this._options.agent ?? false,
				closeOnBeforeunload: false
			}
		);

		this.state = WEB_SOCKET_GATEWAY_STATES.CONNECTING;

		socket.on( 'connect', () => {
			this._debugEvent( 'connect' );

			this.socketId = socket.id;
		} );
		socket.on( 'connect_error', ( error: IPublicError ) => {
			this._debugEvent( 'connect_error', error );
		} );
		socket.on( 'disconnect', () => {
			this._debugEvent( 'disconnect' );

			this._onDisconnect();
		} );

		socket.io.on( 'reconnect', async () => {
			this._debugEvent( 'reconnect' );

			await this._onReconnect();
		} );
		socket.io.on( 'reconnect_attempt', ( attempt: number ) => {
			this._debugEvent( 'reconnect_attempt', attempt );

			this.state = WEB_SOCKET_GATEWAY_STATES.CONNECTING;

			this._connectionAttempt = attempt;
		} );

		socket.on( 'unauthorized', ( data: { error: Error; } ) => {
			this._debugEvent( 'unauthorized' );

			this._onUnauthorized( data );
		} );
		socket.on( 'authenticationRequest', async ( data: { attempt: number; } ) => {
			this._debugEvent( 'authenticationRequest', data.attempt );

			await this._onReconnect();
		} );

		this._socket = socket;

		return socket;
	}

	/**
	 * Emits event via socket.
	 */
	private _emit<TResponse extends IMessage>(
		eventName: string | number,
		buffer: ArrayBuffer | { token: ArrayBuffer | string; }
	): Promise<TResponse> {
		const socket: ISocket = this._socket!;

		return this._requestsManager.send(
			req => {
				socket.emit(
					// types for socket.io allows only to use string
					eventName as string,
					buffer,
					( error: IPublicError, response?: IMessage ) => {
						if ( error ) {
							return req.error( CKEditorCloudServicesServerError.fromPublicError( error ) );
						}

						req.response( response as TResponse );
					}
				);
			},
			this._options.requestTimeout
		);
	}

	private _addAuthData( environmentId: string, userId: string ): void {
		this._socketAuth = {
			environmentId,
			userId,
			isAuthenticated: true
		};
	}

	private _removeAuthData(): void {
		this._socketAuth = undefined;
	}

	/**
	 * Socket.io `connect` events handler.
	 */
	private async _onConnect(): Promise<void> {
		await this._authenticate( this._token.value );

		this.state = WEB_SOCKET_GATEWAY_STATES.CONNECTED;

		const onTokenValueChangeCallback: TokenChangeValueEventCallback =
			async ( event, name, value ) => {
				this._debugEvent( 'token:value:change' );

				try {
					await this._authenticate( value );
				} catch ( error ) {
					// ignore
				}
			};

		this._token.on( 'change:value', onTokenValueChangeCallback );

		// Stops listening on error, because after first successful connection we allow for unlimited reconnect attempts
		this._socket!.io.off( 'reconnect_error' );

		this.on( 'disconnect', () => {
			this._token.off( 'change:value', onTokenValueChangeCallback );
		} );
	}

	/**
	 * Socket.io `reconnect` events handler.
	 */
	private async _onReconnect(): Promise<void> {
		await this._token.refreshToken();
		await this._onConnect();
	}

	/**
	 * Socket.io `disconnect` events handler.
	 */
	private _onDisconnect(): void {
		this.state = WEB_SOCKET_GATEWAY_STATES.DISCONNECTED;
		this._connectionAttempt = 0;

		this.fire( 'disconnect' );

		for ( const channel of this._channels.values() ) {
			channel.remove();
		}

		this._channels.clear();

		if ( this._options.autoReconnect !== undefined && !this._options.autoReconnect ) {
			this._socket = undefined;
		}
	}

	private _debugEvent( eventName: string, eventData?: unknown ): void {
		if ( !this._isDebugModeEnabled() ) {
			return;
		}

		const eventDataString: string = eventData !== undefined ? `, data: ${ eventData }` : '';

		console.info( `${ new Date().toLocaleString() } ${ eventName }${ eventDataString }` );
	}

	/**
	 * Socket.io `reconnect_error` events handler.
	 */
	private _reconnectionAttemptError( orError: ( error: CKEditorCloudServicesError ) => void ): void {
		// The mechanism throws an error after 3 failed initial reconnects.
		// When the client was already connected to the socket, looses connection
		// ex. The Internet instability, then retries are continued until successful connection.
		// First connect doesn't count as reconnect
		if ( this._connectionAttempt >= INITIAL_CONNECTION_ATTEMPTS - 1 ) {
			this.disconnect();

			orError( CKEditorCloudServicesError.fromPublicError( {
				message: 'The number of initial connection attempts exceeded.',
				// eslint-disable-next-line max-len
				explanation: 'Three initial connection attempts failed. It can be caused by a slow, unstable, missing or blocked Internet connection.',
				// eslint-disable-next-line max-len
				action: 'Please verify the stability of your Internet connection and ensure that no antivirus or firewall software blocks the Web Socket protocol connections.'
			} ) );
		}
	}

	/**
	 * `onUnauthorized` events handler.
	 */
	private _onUnauthorized( { error }: { error: Error; } ): void {
		this._removeAuthData();

		this.fire( 'error', CKEditorCloudServicesServerError.fromPublicError( error ) );
	}

	/**
	 * Authenticates socket.
	 */
	private async _authenticate( token: ArrayBuffer | string ): Promise<void> {
		try {
			this._debugEvent( 'authenticate:start' );

			const message: IAuthMessage = await this._emit<IAuthMessage>( 2, { token } );

			this._debugEvent( 'authenticate:success', `envId: ${ message.environmentId }, userId: ${ message.userId }` );

			this._addAuthData( message.environmentId, message.userId );
		} catch ( error ) {
			this._debugEvent( 'authenticate:error', error.message );

			this._removeAuthData();

			throw error;
		}
	}

	private _isDebugModeEnabled( ): boolean {
		if ( !global.window.localStorage ) {
			return false;
		}

		const isDebugModeEnabledString: string = global.window.localStorage.getItem( 'csClientDebugMode' ) ?? 'false';

		return isDebugModeEnabledString.toLowerCase() === 'true';
	}
}

export default WebSocketGateway;
